// Thin wrapper exposing the active locale + setter through a single hook
// (`useLocale`). Sits above the rest of the app so any component can read
// or change the language without reaching into i18next directly.
//
// Public (anonymous) callers can set the locale freely; tenant-wide changes
// from inside the authenticated app go through a separate API mutation and
// then call `setLocale` to broadcast the new value to the UI.

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from './index';
import {
  FALLBACK_LOCALE,
  LOCALE_STORAGE_KEY,
  type Locale,
  normalizeLocale,
} from './config';

interface LocaleContextValue {
  locale: Locale;
  setLocale: (next: Locale, options?: { persist?: boolean }) => Promise<void>;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

function readInitialLocale(): Locale {
  if (typeof window === 'undefined') return FALLBACK_LOCALE;
  const fromStorage = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  if (fromStorage) return normalizeLocale(fromStorage);
  return normalizeLocale(i18n.language);
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  // i18next's own language detector populates `i18n.language` before we mount,
  // so this initial read is already correct on first paint.
  const [locale, setLocaleState] = useState<Locale>(readInitialLocale);
  const { i18n: i18nInstance } = useTranslation();

  // Keep React state in sync if anything else (HMR, tenant fetch, dev tools)
  // calls `i18n.changeLanguage` directly.
  useEffect(() => {
    const onChange = (lng: string) => setLocaleState(normalizeLocale(lng));
    i18nInstance.on('languageChanged', onChange);
    return () => {
      i18nInstance.off('languageChanged', onChange);
    };
  }, [i18nInstance]);

  const setLocale = useCallback<LocaleContextValue['setLocale']>(
    async (next, options) => {
      const normalized = normalizeLocale(next);
      if (normalizeLocale(i18nInstance.language) === normalized) {
        if (options?.persist !== false && typeof window !== 'undefined') {
          window.localStorage.setItem(LOCALE_STORAGE_KEY, normalized);
          document.cookie = `${LOCALE_STORAGE_KEY}=${normalized}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
        }
        document.documentElement.lang = normalized;
        return;
      }
      await i18nInstance.changeLanguage(normalized);
      // i18next's detector caches via its own plugin, but we write through
      // explicitly so the cookie is set on the server-readable path/sameSite
      // even when localStorage is the active source.
      if (options?.persist !== false && typeof window !== 'undefined') {
        window.localStorage.setItem(LOCALE_STORAGE_KEY, normalized);
        document.cookie = `${LOCALE_STORAGE_KEY}=${normalized}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
      }
      document.documentElement.lang = normalized;
    },
    [i18nInstance],
  );

  // Reflect the active locale on <html lang> so the browser, screen readers,
  // and our CSS selectors all see the same source of truth.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error('useLocale must be used inside <LocaleProvider>');
  }
  return ctx;
}
